import { z } from 'zod';
import { ErrorCode } from '../../../errors/codes.js';

// ---- v2 agent error vocabulary ----------------------------------------------
//
// WHY (#2006, epic #2004): the v2 wire (`error` event + errored
//   `tool_call_result`) must accept ONLY the agent's own error codes — never the
//   full global `ErrorCode` enum, which carries unrelated domain codes
//   (PHONE_ALREADY_EXISTS, cargo-payment codes) and generic VALIDATION/INTERNAL
//   codes. Pinning the wire to this 17-code subset means a stale or misrouted
//   non-agent code is rejected at parse-time instead of leaking onto the client.

/**
 * The agent-scoped subset of `ErrorCode`: the 4 v1 `AGENT_*` codes plus the 13
 * added by #2006 (17 total). This is the ONLY error vocabulary on the v2 wire.
 */
export const AGENT_ERROR_CODES = [
  ErrorCode.AGENT_NOT_CONFIGURED,
  ErrorCode.AGENT_SQL_REJECTED,
  ErrorCode.AGENT_SQL_TIMEOUT,
  ErrorCode.AGENT_RUN_FAILED,
  ErrorCode.AGENT_MODEL_TIMEOUT,
  ErrorCode.AGENT_MODEL_UPSTREAM_ERROR,
  ErrorCode.AGENT_MODEL_MALFORMED_RESPONSE,
  ErrorCode.AGENT_TOOL_FAILED,
  ErrorCode.AGENT_SQL_RESULT_TOO_LARGE,
  ErrorCode.AGENT_UNSUPPORTED_DOMAIN,
  ErrorCode.AGENT_NEEDS_CLARIFICATION,
  ErrorCode.AGENT_SCHEMA_UNKNOWN,
  ErrorCode.AGENT_PERMISSION_DENIED,
  ErrorCode.AGENT_INTERNAL_ERROR,
  ErrorCode.AGENT_BUDGET_EXCEEDED,
  ErrorCode.AGENT_ZOD_PARSE_ERROR,
  ErrorCode.AGENT_STREAM_ABORTED,
] as const;

/**
 * @aiPurpose Zod enum of the agent-scoped error vocabulary — the agent subset of
 *   the global `ErrorCode`. Its inferred output type is the union of the 17
 *   `AGENT_*` members of `ErrorCode`.
 * @aiInvariant (#2006, epic #2004) this is the ONLY error vocabulary permitted on
 *   the v2 wire (`error` event `code` + errored `tool_call_result` `errorCode`).
 *   A non-agent `ErrorCode` (e.g. INTERNAL_ERROR, PHONE_ALREADY_EXISTS) is
 *   rejected at parse-time.
 */
export const AgentErrorCodeSchema = z.enum(AGENT_ERROR_CODES);

export type AgentErrorCode = z.infer<typeof AgentErrorCodeSchema>;
